import React, { useMemo, type ReactNode } from 'react';
import { Box } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import { MAPPING_FIELDS_INFO_LINK } from '../../../../common/constants';
import {
	EMPTY_SUMMARY,
	GENERIC_ERRORS,
	INVALID_CASCADE_SELECT_FORMAT,
	INVALID_COMMENT_FORMAT,
	INVALID_DATE,
	INVALID_DATETIME,
	INVALID_ISSUE_ID,
	INVALID_NUMBER,
	INVALID_USER_ID,
	INVALID_WORKLOG_FORMAT,
} from '../../../../common/constants/errors';
import type { ColumnName } from '../../../../common/types';
import { useMappingState } from '../../../../controllers/import-state';
import messages from './messages';

export const useValidationError = (columnName: ColumnName) => {
	const [mappingState] = useMappingState();
	const { formatMessage } = useIntl();
	const error = mappingState?.validationError?.columnErrors?.[columnName] || null;

	const errorMap = useMemo(
		() => ({
			[INVALID_DATE]: formatMessage(messages.dateFormatError),
			[INVALID_DATETIME]: formatMessage(messages.dateTimeFormatError),
			[INVALID_NUMBER]: formatMessage(messages.numericalFormatError),
			[INVALID_CASCADE_SELECT_FORMAT]: formatMessage(messages.cascadingFormatError),
			[EMPTY_SUMMARY]: formatMessage(messages.emptySummaryError),
			[INVALID_ISSUE_ID]: formatMessage(messages.issueIdFormatError),
			[GENERIC_ERRORS]: formatMessage(messages.genericError),
			[INVALID_USER_ID]: formatMessage(messages.genericError),
			[INVALID_COMMENT_FORMAT]: formatMessage(messages.genericError),
			[INVALID_WORKLOG_FORMAT]: formatMessage(messages.worklogFormatError, {
				link: (content: ReactNode) => (
					<Box as="a" href={MAPPING_FIELDS_INFO_LINK} target="_blank">
						{content}
					</Box>
				),
			}),
		}),
		[formatMessage],
	);

	return {
		hasValidationError: !!error,
		validationMessage: error && (errorMap[error] || formatMessage(messages.genericError)),
	};
};
